import * as React from 'react';
import Box from '@mui/material/Box';

import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';

import CollapseTop from '@portal/assets/svg/collapse_top.svg?react';
import CollapseBottom from '@portal/assets/svg/collapse_bottom.svg?react';

import DetailDots from '@portal/assets/svg/manager-user/ic_detail_dots.svg?react';

import { Checkbox, Col, Dropdown, Menu, Row, Space, Switch } from 'antd';
import useReduxState from '@portal/hooks/useReduxState';
import { ListGroup, TypeRemit } from '@portal/models/reducers';
import { translate } from '@portal/services/i18n';
import RemitModal from '../RemitModal/RemitModal';
import { useDispatch } from 'react-redux';
import { listAllGroups } from '@portal/actions/group';

interface RowRemit {
  minLimit: number;
  maxLimit: number;
  approvers: ListGroup[];
  minApprovers: number;
}
interface Remit {
  rows: RowRemit[];
}
interface History {
  item: string;
  uid: string;
  active: boolean;
  remit?: Remit;
}
interface Data {
  name: string;
  active: boolean;
  history: History[];
}

function TopRow(props: {
  row: Data;
  pos: number;
  rows: Data[];
  setRows: React.Dispatch<React.SetStateAction<Data[]>>;
}) {
  const { row, pos, rows, setRows } = props;
  const [open, setOpen] = React.useState(false);

  const dispatch = useDispatch();

  const toggleRow = () => {
    const asActive = !row.active;

    const newValue = {
      ...row,
      active: asActive,
      history: asActive === false ? toggleFalseAllHistory() : [...row.history],
    };
    const arrayOfRows = [...rows];
    arrayOfRows[pos] = newValue;
    setRows(arrayOfRows);
  };

  const toggleFalseAllHistory = () => {
    const newHistory = row.history.map((item): History => {
      return {
        ...item,
        active: false,
      };
    });

    return newHistory;
  };

  const toggleHistory = (posHistory: number) => {
    const asActive = !row.history[posHistory].active;

    const arrayOfHistory = [...row.history];

    arrayOfHistory[posHistory].active = asActive;

    const newValue = {
      ...row,
      history: arrayOfHistory,
    };
    const arrayOfRows = [...rows];
    arrayOfRows[pos] = newValue;
    setRows(arrayOfRows);
  };

  React.useEffect(() => {
    dispatch(listAllGroups(100, 0, ''));
  }, [dispatch]);

  return (
    <>
      <Row className="table-mock__body__row-item">
        <Col sm={12}>
          <Switch
            size="small"
            defaultChecked
            onChange={() => {
              if (open === false && !row.active) {
                setOpen(!open);
              }
              toggleRow();
            }}
            checked={row.active}
          />
          <span style={{ marginLeft: 24 }}>{row.name}</span>
        </Col>
        <Col sm={10} />
        <Col sm={1}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <CollapseBottom /> : <CollapseTop />}
          </IconButton>
        </Col>
        <Col>
          <a>
            <Space>
              <DetailDots />
            </Space>
          </a>
        </Col>
      </Row>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box>
          {row?.history.map((historyRow, index) => (
            <PaymentLine
              historyRow={historyRow}
              index={index}
              row={row}
              rows={rows}
              toggleHistory={toggleHistory}
              pos={pos}
              key={historyRow.uid}
            />
          ))}
        </Box>
      </Collapse>
    </>
  );
}

function PaymentLine(props: {
  historyRow: History;
  row: Data;
  pos: number;
  toggleHistory: (index: number) => void;
  index: number;
  rows: Data[];
}) {
  const { historyRow, row, pos, toggleHistory, index, rows } = props;

  const [openModalRemit, setOpenModalRemit] = React.useState(false);
  const [titleRemit, setTitleRemit] = React.useState('');
  const [typeRemit, setTypeRemit] = React.useState<TypeRemit>('transfer');

  const MenuSettings = (history: History) => (
    <Menu>
      <Menu.Item
        onClick={() => {
          setTitleRemit(history.item);
          setTypeRemit(history.uid as TypeRemit);
          setOpenModalRemit(true);
        }}
      >
        {translate('PAGES.PROFILE_MANAGER.BUTTONS_ACTIONS.NEW_REMIT')}
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Row className="table-mock__body__row-subitem" key={historyRow.item}>
        <Col sm={22}>
          <Checkbox
            checked={historyRow.active}
            disabled={!row.active}
            onChange={() => toggleHistory(index)}
          >
            {historyRow.item}
          </Checkbox>
        </Col>
        <Col sm={1} />
        {pos === rows.length - 1 && (
          <Dropdown
            disabled={!historyRow.active}
            overlay={MenuSettings(historyRow)}
            trigger={['click']}
          >
            <a onClick={(e) => e.preventDefault()}>
              <Space>
                <DetailDots />
              </Space>
            </a>
          </Dropdown>
        )}
      </Row>
      <RemitModal
        type={typeRemit}
        title={titleRemit}
        visible={openModalRemit}
        setVisible={setOpenModalRemit}
      />
    </>
  );
}

export default function CollapsibleTable(props: {
  rows: Data[];
  setRows: React.Dispatch<React.SetStateAction<Data[]>>;
}) {
  const { rows, setRows } = props;

  const {
    user: { permissionsList },
  } = useReduxState();

  const rowsMemo = React.useMemo((): History[] => {
    const data: History[] = [];

    permissionsList?.forEach((item) => {
      data.push({
        item: item?.name ?? '',
        active: false,
        uid: item.value ?? '',
      });
    });

    return data;
  }, [permissionsList]);

  React.useEffect(() => {
    if (permissionsList.length > 0) {
      setRows([
        { name: 'Serviços', active: false, history: rowsMemo },
        ...rows,
      ]);
    }
  }, [permissionsList, rows, rowsMemo, setRows]);

  return (
    <>
      <Row className="table-mock__header">
        <Col className="table-mock__header__col-text-start" md={12} sm={12}>
          {translate(
            'SCREENS.PROFILE_MANAGER.TABLE_HEADER.ACTIVER_PERMISSIONS'
          )}
        </Col>
        <Col className="table-mock__header__col-text" md={9} sm={9}>
          {translate('SCREENS.PROFILE_MANAGER.TABLE_HEADER.APPROVERS')}
        </Col>
        <Col md={1} sm={1} />
        <Col className="table-mock__header__col-text" md={2} sm={2}>
          {translate('SCREENS.PROFILE_MANAGER.TABLE_HEADER.DETAILS')}
        </Col>
      </Row>

      {rows.map((item, index) => (
        <TopRow
          rows={rows}
          key={index}
          row={item}
          pos={index}
          setRows={setRows}
        />
      ))}
    </>
  );
}
